import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { GreenSwitch } from '../Hospital/HospitalContact';

function EmailNotification({ user, handleAccess, setAuthPop, setWarning, setAlert }) {
    const [emailData, setEmailData] = useState([]);
    const [search, setSearch] = useState(null);
    const [loading, setLoading] = useState(false);
    
    // Due popup states
    const [duePopupOpen, setDuePopupOpen] = useState(false);
    const [dueData, setDueData] = useState([]);
    const [dueLoading, setDueLoading] = useState(false);
    const [selectedClientName, setSelectedClientName] = useState('');

    const itemsPerPage = 10;
    const [currentPage, setCurrentPage] = useState(1);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const filteredData = emailData?.filter((data) => {
        if (search === null || search === '') {
            return data;
        }
        const searchLower = search?.toString().toLowerCase();
        if (
            data.client_id?.toString().toLowerCase().includes(searchLower) ||
            data.client_name?.toString().toLowerCase().includes(searchLower) ||
            data.group_name?.toString().toLowerCase().includes(searchLower) ||
            data.billing_type?.toString().toLowerCase().includes(searchLower) ||
            (data.saas_only ? 'yes' : 'no').includes(searchLower) ||
            data.onboarded_at?.toString().toLowerCase().includes(searchLower) ||
            data.status?.toString().toLowerCase().includes(searchLower) ||
            data.due?.toString().toLowerCase().includes(searchLower)
        ) {
            return data;
        }
        return false;
    });

    const totalPages = Math.ceil((filteredData?.length || 0) / itemsPerPage);

    useEffect(() => {
        fetchEmailNotificationData();
    }, []);


    const fetchEmailNotificationData = async () => {
        try {
            setLoading(true);
            const { REACT_APP_GET_CLIENT_MAIL_NOTIFICATION_INSIGHTS } = process.env;
            console.log('API URL:', REACT_APP_GET_CLIENT_MAIL_NOTIFICATION_INSIGHTS);
            
            if (!REACT_APP_GET_CLIENT_MAIL_NOTIFICATION_INSIGHTS) {
                console.error('REACT_APP_GET_CLIENT_MAIL_NOTIFICATION_INSIGHTS is not defined');
                setWarning('API URL is not configured');
                return;
            }
            
            const response = await axios.get(REACT_APP_GET_CLIENT_MAIL_NOTIFICATION_INSIGHTS);
            console.log('API Response:', response);
            
            if (response.status === 200) {
                setEmailData(response.data?.data || response.data || []);
            }
        } catch (err) {
            console.log('API Error:', err);
            setWarning('Failed to fetch email notification data');
        } finally {
            setLoading(false);
        }
    };

    // Fetch Due Data
    const fetchDueData = async (clientId, clientName) => {
        try {
            setDueLoading(true);
            setSelectedClientName(clientName);
            setDuePopupOpen(true);
            
            const { REACT_APP_GET_DUE_DATA } = process.env;
            
            if (!REACT_APP_GET_DUE_DATA) {
                console.error('REACT_APP_GET_DUE_DATA is not defined');
                setWarning('Due API URL is not configured');
                return;
            }
            
            const response = await axios.get(`${REACT_APP_GET_DUE_DATA}?client_fk=${clientId}`);
            
            if (response.status === 200) {
                setDueData(response.data?.data || response.data || []);
            }
        } catch (err) {
            console.log('Due API Error:', err);
            setWarning('Failed to fetch due data');
        } finally {
            setDueLoading(false);
        }
    };

    const handleCloseDuePopup = () => {
        setDuePopupOpen(false);
        setDueData([]);
        setSelectedClientName('');
    };

    // Calculate total amount
    const totalAmount = dueData.reduce((sum, item) => sum + (parseFloat(item.due) || 0), 0);

    const dateBodyTemplate = (rowData) => {
        if (!rowData) return '-';
        const formattedDate = new Date(rowData).toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
        return formattedDate;
    };

    const handleToggle = async (clientId, currentStatus) => {
        try {
            if (!handleAccess('HospitalNotice', 'Email Notification Toggle')) {
                setAuthPop(true);
                return;
            }

            const { REACT_APP_UPDATE_DUE_DATA } = process.env;
            
            if (!REACT_APP_UPDATE_DUE_DATA) {
                console.error('REACT_APP_UPDATE_DUE_DATA is not defined');
                setWarning('Update API URL is not configured');
                return;
            }

            // status: 1 for enable, 2 for disable
            const newStatus = currentStatus ? 2 : 1;
            
            const response = await axios.post(REACT_APP_UPDATE_DUE_DATA, {
                client_id: clientId,
                status: newStatus
            });

            if (response.status === 200) {
                // Update local state with boolean
                setEmailData(prevData =>
                    prevData.map(item =>
                        item.client_id === clientId
                            ? { ...item, automation_restriction: !currentStatus }
                            : item
                    )
                );
                
                // Show success message
                if (newStatus === 1) {
                    setAlert('Email notification enabled successfully');
                } else {
                    setAlert('Email notification disabled successfully');
                }
            } else {
                setWarning('Failed to update email notification status');
            }
        } catch (err) {
            console.log(err);
            setWarning('Failed to update email notification status');
        }
    };

    return (
        <div>
            <div className='notice-filters' style={{ justifyContent: 'flex-end', marginTop: '20px' }}>
                <TextField
                    className='search-div'
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    id="outlined-basic"
                    label="Search"
                    variant="outlined"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            <div className='email-notification-table'>
                <div className='email-notification-table-head'>
                    <div>Client ID</div>
                    <div>Client Name</div>
                    <div>Group Name</div>
                    <div>Billing Type</div>
                    <div>Saas Only</div>
                    <div>Onboarded At</div>
                    <div>Status</div>
                    <div>Due</div>
                    <div>Action</div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
                ) : (
                    filteredData?.slice(indexOfFirstItem, indexOfLastItem)?.map((data, id) => (
                        <div key={id} className='email-notification-table-body'>
                            <div>{data?.client_id}</div>
                            <div>{data?.client_name}</div>
                            <div>{data?.group_name}</div>
                            <div>{data?.billing_type}</div>
                            <div>{data?.saas_only ? 'Yes' : 'No'}</div>
                            <div>{dateBodyTemplate(data?.onboarded_at)}</div>
                            <div>{data?.status}</div>
                            <div 
                                onClick={() => fetchDueData(data?.client_id, data?.client_name)}
                                style={{ 
                                    cursor: 'pointer', 
                                    color: '#0059B2', 
                                    textDecoration: 'underline',
                                    fontWeight: '500',
                                    fontWeight: 'bold'
                                }}
                            >
                               ₹ {data?.due}
                            </div>
                            <div>
                                <GreenSwitch
                                    checked={data?.automation_restriction === true}
                                    onChange={() => handleToggle(data?.client_id, data?.automation_restriction)}
                                    color="secondary"
                                />
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div style={{ float: 'right', marginRight: '20px', marginTop: '10px' }}>
                <Button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                >
                    Previous
                </Button>
                <span>{`${currentPage} of ${totalPages || 1}`}</span>
                <Button
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage(currentPage + 1)}
                >
                    Next
                </Button>
            </div>

            {/* Due Details Popup */}
            <Dialog 
                open={duePopupOpen} 
                onClose={handleCloseDuePopup}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Due Details - {selectedClientName}
                    <IconButton onClick={handleCloseDuePopup}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {dueLoading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
                    ) : (
                        <>
                            <div className='due-popup-table'>
                                <div className='due-popup-table-head'>
                                    <div>Reference No</div>
                                    <div>Amount</div>
                                </div>
                                {dueData.length > 0 ? (
                                    dueData.map((item, index) => (
                                        <div key={index} className='due-popup-table-body'>
                                            <div>{item?.reference_no}</div>
                                            <div>₹ {parseFloat(item?.due || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>No data found</div>
                                )}
                            </div>
                            {dueData.length > 0 && (
                                <div className='due-popup-total'>
                                    <div><strong>Total Amount</strong></div>
                                    <div><strong>₹ {totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></div>
                                </div>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDuePopup} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default EmailNotification;